/**
 * CommuteRouteMap — mini-mappa statica del tragitto pendolare del driver.
 *
 * Phase 31 — Commuter Delivery Network.
 *
 * Props:
 *  - startLat, startLng (number | null)
 *  - endLat, endLng (number | null)
 *  - startLabel, endLabel (string)
 *  - maxDetourKm (number)  → cerchio di tolleranza intorno a start ed end
 *  - className (string, opz.)
 *
 * Strategia:
 *  - Se coords presenti e REACT_APP_GOOGLE_MAPS_API_KEY disponibile → Google Static Maps
 *    con path A→B + 2 cerchi (approssimati come polilinee chiuse a 32 punti)
 *  - Se coords mancanti o immagine fallisce a caricarsi → fallback SVG "schematico"
 *
 * Nessun JS Maps loader: usa <img> statico, pesa pochi KB e funziona on-mobile.
 */
import { useState } from "react";
import { MapPin, Navigation } from "lucide-react";
import { getBrowserMapsKey } from "@/lib/maps";

const STATIC_BASE = "https://maps.googleapis.com/maps/api/staticmap";

// Approssimazione cerchio terra (1° lat ≈ 111.32 km)
function circlePoints(lat, lng, radiusKm, steps = 24) {
  const points = [];
  const latRad = (lat * Math.PI) / 180;
  const kmPerDegLat = 110.574;
  const kmPerDegLng = 111.32 * Math.cos(latRad);
  for (let i = 0; i <= steps; i++) {
    const t = (i / steps) * 2 * Math.PI;
    const dLat = (radiusKm * Math.cos(t)) / kmPerDegLat;
    const dLng = (radiusKm * Math.sin(t)) / kmPerDegLng;
    points.push([lat + dLat, lng + dLng]);
  }
  return points;
}

function buildStaticMapUrl({ startLat, startLng, endLat, endLng, maxDetourKm, key, width, height }) {
  const params = new URLSearchParams();
  params.set("size", `${width}x${height}`);
  params.set("scale", "2");
  params.set("maptype", "roadmap");
  params.set("language", "it");
  params.set("region", "CH");
  params.set("key", key);

  // Markers
  params.append("markers", `color:0x0052FF|label:A|${startLat},${startLng}`);
  params.append("markers", `color:0xE53935|label:B|${endLat},${endLng}`);

  // Straight path A→B (no Directions API: keep light)
  params.append("path", `color:0x0052FFcc|weight:4|geodesic:true|${startLat},${startLng}|${endLat},${endLng}`);

  // Circles via closed polyline
  if (maxDetourKm > 0) {
    const fmt = (pts) => pts.map(([la, ln]) => `${la.toFixed(5)},${ln.toFixed(5)}`).join("|");
    const cs = circlePoints(startLat, startLng, maxDetourKm);
    const ce = circlePoints(endLat, endLng, maxDetourKm);
    params.append("path", `color:0x10B98166|weight:1|fillcolor:0x10B98122|${fmt(cs)}`);
    params.append("path", `color:0x10B98166|weight:1|fillcolor:0x10B98122|${fmt(ce)}`);
  }

  return `${STATIC_BASE}?${params.toString()}`;
}

/**
 * Phase 44.31.6 — Fallback con mappa reale address-based.
 * Quando lat/lng mancano ma abbiamo gli indirizzi testuali, Google Static Maps
 * accetta location stringhe e geocoda lato server. Risultato: mappa reale
 * (no più "ANTEPRIMA SCHEMATICA") anche senza coords salvate sul DB.
 */
function buildStaticMapUrlByAddress({ startAddress, endAddress, key, width, height }) {
  const sa = encodeURIComponent(startAddress);
  const ea = encodeURIComponent(endAddress);
  const params = new URLSearchParams();
  params.set("size", `${width}x${height}`);
  params.set("scale", "2");
  params.set("maptype", "roadmap");
  params.set("language", "it");
  params.set("region", "CH");
  params.set("key", key);
  // Markers per address: Google li geocoda + auto-fits bounds
  // I `markers` accettano la stringa di location come ultimo segmento.
  return `${STATIC_BASE}?${params.toString()}&markers=color:0x0052FF%7Clabel:A%7C${sa}&markers=color:0xE53935%7Clabel:B%7C${ea}`;
}

export default function CommuteRouteMap({
  startLat,
  startLng,
  endLat,
  endLng,
  startLabel = "Partenza",
  endLabel = "Arrivo",
  maxDetourKm = 5,
  className = "",
}) {
  const [imgFailed, setImgFailed] = useState(false);
  const [imgFailedAddr, setImgFailedAddr] = useState(false);
  const hasCoords =
    Number.isFinite(startLat) && Number.isFinite(startLng) &&
    Number.isFinite(endLat) && Number.isFinite(endLng);
  const hasAddresses = Boolean(startLabel && endLabel && startLabel !== "Partenza" && endLabel !== "Arrivo");
  const mapsKey = getBrowserMapsKey();

  const showStaticByCoords = hasCoords && mapsKey && !imgFailed;
  const showStaticByAddr = !showStaticByCoords && hasAddresses && mapsKey && !imgFailedAddr;

  return (
    <div
      className={`relative overflow-hidden rounded-2xl border border-dapnow-line bg-dapnow-bg ${className}`}
      data-testid="commute-route-map"
    >
      {showStaticByCoords ? (
        <img
          src={buildStaticMapUrl({
            startLat, startLng, endLat, endLng,
            maxDetourKm: Math.max(0, Math.min(maxDetourKm || 0, 50)),
            key: mapsKey,
            width: 600, height: 220,
          })}
          alt={`Tragitto pendolare ${startLabel} → ${endLabel}`}
          loading="lazy"
          onError={() => setImgFailed(true)}
          className="w-full h-[180px] sm:h-[200px] object-cover"
          data-testid="commute-route-map-img"
        />
      ) : showStaticByAddr ? (
        <img
          src={buildStaticMapUrlByAddress({
            startAddress: startLabel,
            endAddress: endLabel,
            key: mapsKey,
            width: 600, height: 220,
          })}
          alt={`Tragitto pendolare ${startLabel} → ${endLabel}`}
          loading="lazy"
          onError={() => setImgFailedAddr(true)}
          className="w-full h-[180px] sm:h-[200px] object-cover"
          data-testid="commute-route-map-img-addr"
        />
      ) : (
        <FallbackSvg
          startLabel={startLabel}
          endLabel={endLabel}
          maxDetourKm={maxDetourKm}
          reason={!hasCoords && !hasAddresses ? "no-coords" : !mapsKey ? "no-key" : "load-failed"}
        />
      )}

      {/* Overlay: pill max_detour + badge usage */}
      <div className="absolute top-2 left-2 right-2 flex items-start justify-between gap-2 pointer-events-none">
        <span
          className="inline-flex items-center gap-1 rounded-full bg-white/95 backdrop-blur text-dapnow-ink px-2.5 py-1 text-[11px] font-semibold shadow-sm"
          data-testid="commute-detour-pill"
        >
          <Navigation className="h-3 w-3 text-emerald-600" />
          Deviazione massima: {maxDetourKm} km
        </span>
        <span
          className="inline-flex items-center gap-1 rounded-full bg-emerald-600 text-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider shadow-sm"
          data-testid="commute-matching-badge"
        >
          Usato per trovare ordini compatibili
        </span>
      </div>
    </div>
  );
}

function FallbackSvg({ startLabel, endLabel, maxDetourKm, reason }) {
  return (
    <div
      className="w-full h-[180px] sm:h-[200px] relative bg-gradient-to-br from-dapnow-blue-soft/70 to-emerald-50"
      data-testid="commute-route-map-fallback"
    >
      <svg viewBox="0 0 600 200" preserveAspectRatio="none" className="absolute inset-0 w-full h-full">
        {/* grid */}
        <defs>
          <pattern id="cm-grid" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(15,23,42,0.06)" strokeWidth="1" />
          </pattern>
        </defs>
        <rect width="600" height="200" fill="url(#cm-grid)" />

        {/* detour circles */}
        <circle cx="120" cy="100" r="48" fill="rgba(16,185,129,0.15)" stroke="rgba(16,185,129,0.45)" strokeWidth="1.5" strokeDasharray="3 4" />
        <circle cx="480" cy="100" r="48" fill="rgba(16,185,129,0.15)" stroke="rgba(16,185,129,0.45)" strokeWidth="1.5" strokeDasharray="3 4" />

        {/* path */}
        <line x1="120" y1="100" x2="480" y2="100" stroke="#0052FF" strokeWidth="3" strokeLinecap="round" />

        {/* markers */}
        <circle cx="120" cy="100" r="9" fill="#0052FF" stroke="white" strokeWidth="3" />
        <text x="120" y="103" fontFamily="ui-sans-serif" fontSize="11" fontWeight="700" textAnchor="middle" fill="white">A</text>

        <circle cx="480" cy="100" r="9" fill="#E53935" stroke="white" strokeWidth="3" />
        <text x="480" y="103" fontFamily="ui-sans-serif" fontSize="11" fontWeight="700" textAnchor="middle" fill="white">B</text>
      </svg>

      <div className="absolute bottom-2 left-3 right-3 flex items-end justify-between text-[10px] text-dapnow-ink/70 font-semibold">
        <span className="inline-flex items-center gap-1">
          <MapPin className="h-3 w-3 text-dapnow-blue" />
          <span className="truncate max-w-[150px]">{startLabel}</span>
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="truncate max-w-[150px] text-right">{endLabel}</span>
          <MapPin className="h-3 w-3 text-dapnow-red" />
        </span>
      </div>

      <span className="absolute bottom-9 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-wider font-bold text-dapnow-ink/60">
        {reason === "no-coords"
          ? "Anteprima · indirizzo non ancora geocodificato"
          : reason === "no-key"
            ? "Anteprima schematica"
            : "Anteprima schematica"}
      </span>
    </div>
  );
}
